/**
 * The one look for a status strip — ROUTE_INVENTORY Part 1, G6–G9, and the
 * Knowledge Archive's cap bar (§2.10 #6).
 *
 * Every strip is the same NEUTRAL surface: `bg-card`, a bottom border, one
 * height, one padding, one type size, one icon size, one action-button style
 * and one dismiss control. What differs between them is the TONE, and tone is
 * carried by exactly two things — the icon's colour and a 3px accent on the
 * left edge — both from the status tokens. Nothing else changes colour. The
 * banners used to be four tinted strips in four slightly different shapes
 * (text-xs vs text-sm, a solid amber CTA next to a primary one), so two of them
 * side by side read as two products.
 *
 * Tone is never the only signal: the message says what happened, and the icon
 * is `aria-hidden` decoration.
 *
 * `layout='page'` aligns the content with the app's own page column
 * (`max-w-7xl` and the same gutters), because a page-wide strip sits under the
 * navbar and above that column. `layout='inline'` is for a strip INSIDE a page
 * region (the archive's cap bar), which aligns with its region instead.
 *
 * `live` is the strip's own live-region role. `'alert'` for the urgent ones,
 * which may mount already filled (alerts are announced on insertion);
 * `'off'` when a PERMANENT polite region wraps the strip instead, which is how
 * the offline strip is announced — a polite region that arrives already filled
 * is not reliably read out.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';

import { Button, cn } from '@ai-tutor/ui';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

export type PageBannerTone = 'warning' | 'destructive' | 'info';

/** The single action. `to` is an in-app route, `href` leaves the app. */
export type PageBannerAction =
  | { label: string; onClick: () => void }
  | { label: string; to: string }
  | { label: string; href: string };

export interface PageBannerProps {
  tone: PageBannerTone;
  icon: LucideIcon;
  /** What happened. One sentence, sentence case, full stop. */
  message: ReactNode;
  /** What it means or what to do, in muted text. One sentence. */
  detail?: ReactNode;
  action?: PageBannerAction;
  /** Renders the ✕. Omit for a strip that clears itself. */
  onDismiss?: () => void;
  /** Accessible name of the ✕, e.g. "Dismiss billing banner". */
  dismissLabel?: string;
  live?: 'alert' | 'status' | 'off';
  layout?: 'page' | 'inline';
  testId?: string;
}

const ACCENT: Record<PageBannerTone, string> = {
  warning: 'border-l-warning',
  destructive: 'border-l-destructive',
  info: 'border-l-info',
};

// Emphasis tokens: the icon is a meaningful graphic (WCAG 1.4.11, 3:1), and the
// DEFAULT status colours are tuned for solid fills, not for a glyph on a card.
const ICON: Record<PageBannerTone, string> = {
  warning: 'text-warning-emphasis',
  destructive: 'text-destructive-emphasis',
  info: 'text-info-emphasis',
};

/** The phone card's icon chip: the tone as a soft tint behind the glyph. */
const CHIP: Record<PageBannerTone, string> = {
  warning: 'bg-warning/15 text-warning-emphasis',
  destructive: 'bg-destructive/10 text-destructive-emphasis',
  info: 'bg-info/10 text-info-emphasis',
};

function ActionButton({
  action,
  full = false,
}: {
  action: PageBannerAction;
  /** The phone card's full-width 44px button. */
  full?: boolean;
}) {
  // `tap-target`: drawn as a 32px `sm` button, tapped as 44 on a phone. The
  // banner row is `min-h-12`, so the area fits inside it.
  const common = full
    ? { variant: 'outline' as const, className: 'h-11 w-full' }
    : {
        variant: 'outline' as const,
        size: 'sm' as const,
        className: 'tap-target',
      };
  if ('to' in action) {
    return (
      <Button {...common} asChild>
        <Link to={action.to}>{action.label}</Link>
      </Button>
    );
  }
  if ('href' in action) {
    return (
      <Button {...common} asChild>
        <a href={action.href} target='_blank' rel='noopener noreferrer'>
          {action.label}
        </a>
      </Button>
    );
  }
  return (
    <Button {...common} onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

export function PageBanner({
  tone,
  icon: Icon,
  message,
  detail,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  live = 'off',
  layout = 'page',
  testId,
}: PageBannerProps) {
  const phone = useMediaQuery('(max-width: 639.98px)');
  if (layout === 'page' && phone) {
    return (
      <PhoneBannerCard
        tone={tone}
        icon={Icon}
        message={message}
        detail={detail}
        action={action}
        onDismiss={onDismiss}
        dismissLabel={dismissLabel}
        live={live}
        testId={testId}
      />
    );
  }
  return (
    <div
      data-testid={testId}
      data-tone={tone}
      role={live === 'off' ? undefined : live}
      className={cn(
        // `relative` so the opaque surface paints over the page's fixed
        // instrument lattice wherever the strip sits in normal flow (the
        // archive's cap bar); the page-wide strips are inside a fixed z-40
        // container already.
        'relative border-b border-l-[3px] border-b-border bg-card',
        ACCENT[tone]
      )}
    >
      <div
        className={cn(
          'flex min-h-12 items-center gap-3 py-2',
          layout === 'page'
            ? 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8'
            : 'px-4'
        )}
      >
        <Icon
          className={cn('h-4 w-4 flex-shrink-0', ICON[tone])}
          aria-hidden='true'
        />
        <p className='min-w-0 flex-1 text-sm'>
          <span className='text-foreground'>{message}</span>
          {detail && (
            <span className='ml-1.5 text-muted-foreground'>{detail}</span>
          )}
        </p>
        {action && <ActionButton action={action} />}
        {onDismiss && (
          <Button
            variant='ghost'
            size='sm'
            // 32px drawn, 44px tapped on a phone; the 12px gap to the action
            // leaves room for both areas without overlap.
            className='tap-target h-8 w-8 flex-shrink-0 p-0'
            onClick={onDismiss}
            aria-label={dismissLabel}
          >
            <X className='h-4 w-4' aria-hidden='true' />
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * A page-wide banner on a phone: a floating card over the page, not a strip
 * across it. `ShellBanners` floats it under the navbar without pushing the
 * page down.
 *
 * The strip laid icon, text, action and the X side by side, so on a 390px
 * screen the text got about 120px - "Your last / payment didn't / go /
 * through." ran to seven lines and the banner to 200px. Here the message is
 * a title line, the detail sits under it, and the action is one full-width
 * button below both.
 */
function PhoneBannerCard({
  tone,
  icon: Icon,
  message,
  detail,
  action,
  onDismiss,
  dismissLabel,
  live,
  testId,
}: Omit<PageBannerProps, 'layout'> & { dismissLabel: string }) {
  return (
    <div
      data-testid={testId}
      data-tone={tone}
      role={live === 'off' ? undefined : live}
      // Taps pass through the margin to the page; only the card takes them.
      className='pointer-events-none px-3 pt-2'
    >
      <div className='pointer-events-auto relative flex items-start gap-3 rounded-2xl border border-border bg-card p-3 shadow-elevation-2 motion-safe:duration-200 motion-safe:animate-in motion-safe:slide-in-from-top-2'>
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            CHIP[tone]
          )}
        >
          <Icon className='h-4 w-4' aria-hidden='true' />
        </span>
        <div className={cn('min-w-0 flex-1 pt-0.5', onDismiss && 'pr-8')}>
          <p className='text-sm font-semibold text-foreground'>{message}</p>
          {detail && (
            <p className='mt-0.5 text-sm text-muted-foreground'>{detail}</p>
          )}
          {action && (
            <div className='mt-3'>
              <ActionButton action={action} full />
            </div>
          )}
        </div>
        {onDismiss && (
          <Button
            variant='ghost'
            className='absolute right-0.5 top-0.5 h-11 w-11 p-0 text-muted-foreground'
            onClick={onDismiss}
            aria-label={dismissLabel}
          >
            <X className='h-4 w-4' aria-hidden='true' />
          </Button>
        )}
      </div>
    </div>
  );
}
